import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Pause, Play, MonitorPlay, LogIn, LogOut, RefreshCw, Copy, Building2, Users, AlertTriangle, UserCheck, Trash2, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ETAT_CFG = {
    vert: { label: 'Maîtrisée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
    jaune: { label: 'Vigilance', cls: 'bg-yellow-50 text-yellow-700 border-yellow-300', dot: 'bg-yellow-400' },
    orange: { label: 'Tension', cls: 'bg-orange-50 text-orange-700 border-orange-300', dot: 'bg-orange-500' },
    rouge: { label: 'Critique', cls: 'bg-red-50 text-red-700 border-red-300', dot: 'bg-red-500' },
};

const FilterChip = ({ active, onClick, children, testId }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'}`}
    >
        {children}
    </button>
);

export default function ConsolePage() {
    const navigate = useNavigate();
    const token = localStorage.getItem('console-token');
    const [instances, setInstances] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ name: '', admin_email: '', admin_name: '' });
    const [creds, setCreds] = useState(null);
    const [busy, setBusy] = useState(false);
    const [etatFilter, setEtatFilter] = useState('tous');
    const [pcsFilter, setPcsFilter] = useState('tous');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchInstances = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/console/instances`, { headers: { Authorization: `Bearer ${token}` } });
            setInstances(res.data);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                localStorage.removeItem('console-token');
                navigate('/console/login');
            }
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!token) { navigate('/console/login'); return; }
        fetchInstances();
        const i = setInterval(fetchInstances, 30000);
        return () => clearInterval(i);
    }, [token, fetchInstances, navigate]);

    const filtered = useMemo(() => {
        if (!instances) return [];
        return instances.filter(inst => {
            const s = inst.summary || {};
            if (etatFilter !== 'tous' && s.etat_general !== etatFilter) return false;
            if (pcsFilter === 'actif' && !s.pcs_active) return false;
            if (pcsFilter === 'inactif' && s.pcs_active) return false;
            return true;
        });
    }, [instances, etatFilter, pcsFilter]);

    const handleCreate = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await axios.post(`${API}/console/instances`, form, { headers });
            setCreds(res.data.admin_credentials);
            setForm({ name: '', admin_email: '', admin_name: '' });
            toast.success(`Instance "${res.data.instance.name}" créée`);
            fetchInstances();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur création');
        } finally {
            setBusy(false);
        }
    };

    const toggleSuspend = async (inst) => {
        const action = inst.status === 'active' ? 'suspend' : 'reactivate';
        if (action === 'suspend' && !window.confirm(`Suspendre "${inst.name}" ? Ses utilisateurs ne pourront plus se connecter.`)) return;
        try {
            const res = await axios.put(`${API}/console/instances/${inst.slug}/${action}`, {}, { headers });
            toast.success(res.data.message);
            fetchInstances();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const openViewer = async (inst) => {
        try {
            const res = await axios.post(`${API}/console/instances/${inst.slug}/viewer-link`, {}, { headers });
            window.open(`${window.location.origin}/viewer/${res.data.token}`, '_blank');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const impersonate = async (inst) => {
        try {
            const res = await axios.post(`${API}/console/instances/${inst.slug}/impersonate`, {}, { headers });
            localStorage.setItem('token', res.data.access_token);
            toast.success(`Connecté à "${inst.name}" en tant qu'admin`);
            window.location.href = '/dashboard';
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const deleteInstance = async (inst) => {
        const typed = window.prompt(`⚠️ SUPPRESSION DÉFINITIVE de "${inst.name}" : toutes ses données (utilisateurs, personnes, main courante...) seront perdues.\n\nPour confirmer, tapez exactement le nom de la commune :`);
        if (typed === null) return;
        if (typed.trim() !== inst.name) { toast.error('Nom incorrect — suppression annulée'); return; }
        try {
            const res = await axios.delete(`${API}/console/instances/${inst.slug}`, { headers });
            toast.success(res.data.message);
            fetchInstances();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const logout = () => {
        localStorage.removeItem('console-token');
        navigate('/console/login');
    };

    if (!instances) {
        return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
    }

    const pcsCount = instances.filter(i => i.summary?.pcs_active).length;

    return (
        <div className="min-h-screen bg-slate-50 p-4 md:p-8">
            <div className="max-w-[1400px] mx-auto space-y-6" data-testid="console-page">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Console Centrale</h1>
                            <p className="text-xs text-slate-500">{localStorage.getItem('console-name')} · {instances.length} instance(s) · <span className={pcsCount > 0 ? 'text-red-600 font-semibold' : ''}>{pcsCount} PCS actif(s)</span></p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => { setCreds(null); setCreateOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="console-create-instance-btn">
                            <Plus className="w-4 h-4 mr-2" /> Nouvelle instance
                        </Button>
                        <Button variant="outline" onClick={fetchInstances} className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="console-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
                        <Button variant="outline" onClick={logout} className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="console-logout-btn"><LogOut className="w-4 h-4" /></Button>
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3" data-testid="console-filters">
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                        <Filter className="w-3.5 h-3.5" /> Filtres
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500 font-medium">Niveau de maîtrise :</span>
                            <FilterChip active={etatFilter === 'tous'} onClick={() => setEtatFilter('tous')} testId="filter-etat-tous">Tous</FilterChip>
                            {Object.entries(ETAT_CFG).map(([key, cfg]) => (
                                <FilterChip key={key} active={etatFilter === key} onClick={() => setEtatFilter(key)} testId={`filter-etat-${key}`}>
                                    <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${cfg.dot}`}></span>{cfg.label}</span>
                                </FilterChip>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500 font-medium">PCS :</span>
                            <FilterChip active={pcsFilter === 'tous'} onClick={() => setPcsFilter('tous')} testId="filter-pcs-tous">Tous</FilterChip>
                            <FilterChip active={pcsFilter === 'actif'} onClick={() => setPcsFilter('actif')} testId="filter-pcs-actif">
                                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500"></span>PCS actif</span>
                            </FilterChip>
                            <FilterChip active={pcsFilter === 'inactif'} onClick={() => setPcsFilter('inactif')} testId="filter-pcs-inactif">PCS inactif</FilterChip>
                        </div>
                        {(etatFilter !== 'tous' || pcsFilter !== 'tous') && (
                            <span className="text-xs text-slate-400" data-testid="console-filter-count">{filtered.length} résultat(s)</span>
                        )}
                    </div>
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-12 text-slate-400 text-sm bg-white border border-slate-200 rounded-xl" data-testid="console-no-results">
                        Aucune instance ne correspond aux filtres sélectionnés.
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map(inst => {
                        const s = inst.summary || {};
                        const etat = ETAT_CFG[s.etat_general] || ETAT_CFG.vert;
                        const suspended = inst.status === 'suspended';
                        return (
                            <Card key={inst.slug} className={`bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow ${suspended ? 'opacity-60' : ''}`} data-testid={`console-instance-${inst.slug}`}>
                                <CardContent className="p-5 space-y-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                                <h2 className="font-bold text-slate-900 truncate">{inst.name}</h2>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-0.5">{inst.admin_email || 'instance historique'} {inst.is_default && '· principale'}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1.5">
                                            {suspended ? (
                                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-500 border border-slate-300" data-testid={`instance-status-${inst.slug}`}>Suspendue</span>
                                            ) : s.pcs_active ? (
                                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-red-50 text-red-700 border border-red-300 flex items-center gap-1" data-testid={`instance-pcs-${inst.slug}`}>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span> PCS ACTIF
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-500 border border-slate-300" data-testid={`instance-pcs-${inst.slug}`}>PCS inactif</span>
                                            )}
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-medium border flex items-center gap-1 ${etat.cls}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${etat.dot}`}></span> {etat.label}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2 text-center">
                                        <div className="p-2 rounded bg-slate-50 border border-slate-100">
                                            <p className="text-lg font-bold text-slate-900 flex items-center justify-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-purple-500" />{s.active_incidents ?? 0}</p>
                                            <p className="text-[10px] text-slate-500">Incidents</p>
                                        </div>
                                        <div className="p-2 rounded bg-slate-50 border border-slate-100">
                                            <p className="text-lg font-bold text-slate-900 flex items-center justify-center gap-1"><UserCheck className="w-3.5 h-3.5 text-blue-500" />{s.prises_en_charge ?? 0}</p>
                                            <p className="text-[10px] text-slate-500">Pris en charge</p>
                                        </div>
                                        <div className="p-2 rounded bg-slate-50 border border-slate-100">
                                            <p className="text-lg font-bold text-slate-900 flex items-center justify-center gap-1"><Users className="w-3.5 h-3.5 text-cyan-600" />{s.users_count ?? 0}</p>
                                            <p className="text-[10px] text-slate-500">Utilisateurs</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline" onClick={() => openViewer(inst)} disabled={suspended} className="text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50" data-testid={`instance-viewer-${inst.slug}`}>
                                            <MonitorPlay className="w-3.5 h-3.5 mr-1.5" /> Viewer
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => impersonate(inst)} disabled={suspended} className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`instance-impersonate-${inst.slug}`}>
                                            <LogIn className="w-3.5 h-3.5 mr-1.5" /> Connexion admin
                                        </Button>
                                        {!inst.is_default && (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => toggleSuspend(inst)} className={`text-xs ml-auto ${suspended ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`} data-testid={`instance-suspend-${inst.slug}`}>
                                                    {suspended ? <><Play className="w-3.5 h-3.5 mr-1.5" /> Réactiver</> : <><Pause className="w-3.5 h-3.5 mr-1.5" /> Suspendre</>}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => deleteInstance(inst)} className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`instance-delete-${inst.slug}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent className="max-w-md bg-white border-slate-200 text-slate-900" data-testid="console-create-modal">
                        <DialogHeader>
                            <DialogTitle className="text-slate-900">Nouvelle instance (commune)</DialogTitle>
                            <DialogDescription className="text-slate-500">L'instance est créée SANS PCS actif : la commune l'activera elle-même le moment venu.</DialogDescription>
                        </DialogHeader>
                        {creds ? (
                            <div className="space-y-4">
                                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-300 space-y-2" data-testid="console-created-creds">
                                    <p className="text-sm font-semibold text-emerald-700">Instance créée — identifiants provisoires :</p>
                                    <p className="text-sm text-slate-900 font-mono">Email : {creds.email}</p>
                                    <p className="text-sm text-slate-900 font-mono">Mot de passe : {creds.password}</p>
                                    <p className="text-[11px] text-slate-500">Transmettez ces identifiants à la commune. Ils ne seront plus affichés.</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={() => { navigator.clipboard.writeText(`CIVIS+ — Accès administrateur\nEmail: ${creds.email}\nMot de passe: ${creds.password}\nURL: ${window.location.origin}/login`); toast.success('Identifiants copiés'); }} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid="console-copy-creds-btn">
                                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setCreateOpen(false)} className="text-xs ml-auto text-slate-600">Fermer</Button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleCreate} className="space-y-4">
                                <div>
                                    <Label className="text-slate-700 text-sm">Nom de la commune *</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="Ex: Mairie de Grasse" required data-testid="console-instance-name" />
                                </div>
                                <div>
                                    <Label className="text-slate-700 text-sm">Nom de l'administrateur *</Label>
                                    <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="Ex: Jean Dupont" required data-testid="console-admin-name" />
                                </div>
                                <div>
                                    <Label className="text-slate-700 text-sm">Email de l'administrateur *</Label>
                                    <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="bg-white border-slate-300 text-slate-900" placeholder="admin@commune.fr" required data-testid="console-admin-email" />
                                </div>
                                <p className="text-[11px] text-slate-500">Un mot de passe fort sera généré automatiquement et affiché une seule fois.</p>
                                <div className="flex justify-end gap-2">
                                    <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-600">Annuler</Button>
                                    <Button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="console-submit-instance-btn">{busy ? 'Création...' : "Créer l'instance"}</Button>
                                </div>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
